import React, { useState, useCallback, useMemo } from 'react';
import { Segment } from 'tea-component';
import { BiDataValidator } from './utils/validator';
import { EchartsOptionValidator } from './utils/echartsValidator';
import { IValidationReport, TTestMode } from './types';
import InputArea from './components/InputArea';
import ValidationPanel from './components/ValidationPanel';
import ChartPreview from './components/ChartPreview';
import EchartsOptionPreview from './components/EchartsOptionPreview';
import ExampleSelector from './components/ExampleSelector';
import './index.scss';

/** 校验前展示 loading 的延迟时间（ms），提供视觉反馈 */
const VALIDATION_LOADING_DELAY = 100;

/** 模式切换 Segment 选项（常量提升至模块作用域，避免每次渲染重建） */
const MODE_OPTIONS: { value: TTestMode; text: string; }[] = [
  { value: 'biData', text: 'biData 格式' },
  { value: 'echartsOption', text: '原生 ECharts 配置' },
];

const TestBiData: React.FC = () => {
  const [mode, setMode] = useState<TTestMode>('biData');
  const [jsonInput, setJsonInput] = useState<string>('');
  const [validationReport, setValidationReport] = useState<IValidationReport | null>(null);
  // biData 模式：apiData 数组；原生模式：原始 options 对象
  const [chartData, setChartData] = useState<any>(null);
  const [isValidating, setIsValidating] = useState<boolean>(false);

  // 使用 useMemo 避免每次渲染都创建新的 validator 实例
  const biDataValidator = useMemo(() => new BiDataValidator(), []);
  const echartsValidator = useMemo(() => new EchartsOptionValidator(), []);

  /**
   * 处理渲染图表
   */
  const handleRender = useCallback(() => {
    const validator = mode === 'echartsOption' ? echartsValidator : biDataValidator;

    if (!validator) {
      console.error('[TestBiData] Validator 未初始化');
      setValidationReport({
        passed: false,
        canRender: false,
        errors: [{
          level: 'error',
          message: '验证器未初始化',
          details: 'Validator instance is null',
        }],
        warnings: [],
        successes: [],
        timestamp: Date.now(),
      });
      return;
    }

    setIsValidating(true);

    // 延迟执行以显示加载状态
    setTimeout(() => {
      try {
        const report = validator.validate(jsonInput);
        setValidationReport(report);

        if (report.canRender && report.inputData) {
          if (mode === 'echartsOption') {
            // 原生模式：直接传递 options 对象
            setChartData(report.inputData);
          } else {
            // biData 模式：保持数组形态
            const data = Array.isArray(report.inputData) ? report.inputData : [report.inputData];
            setChartData(data);
          }
        } else {
          setChartData(null);
        }
      } catch (error: any) {
        console.error('[TestBiData] 验证过程异常:', error);
        setValidationReport({
          passed: false,
          canRender: false,
          errors: [
            {
              level: 'error',
              message: `验证过程异常: ${error.message}`,
              details: error.stack,
            },
          ],
          warnings: [],
          successes: [],
          timestamp: Date.now(),
        });
        setChartData(null);
      } finally {
        setIsValidating(false);
      }
    }, VALIDATION_LOADING_DELAY);
  }, [mode, jsonInput, biDataValidator, echartsValidator]);

  const chartDataKey = useMemo(() => {
    if (chartData == null) return '';
    return JSON.stringify(chartData);
  }, [chartData]);

  /**
   * 处理清空
   */
  const handleClear = useCallback(() => {
    setJsonInput('');
    setValidationReport(null);
    setChartData(null);
  }, []);

  /**
   * 切换模式（同步清理上一模式的输入与结果，避免两种格式混淆）
   * @description 不要把 setJsonInput / setValidationReport / setChartData 放进 setMode 的 updater 里：
   *   updater 必须是纯函数；React 18 严格模式 / Concurrent 渲染下可能被调用多次，会触发多次副作用。
   */
  const handleModeChange = useCallback((nextMode: TTestMode) => {
    if (mode === nextMode) return;
    setMode(nextMode);
    setJsonInput('');
    setValidationReport(null);
    setChartData(null);
  }, [mode]);

  /** Segment onChange 适配（参数为 string，需转为 TTestMode） */
  const handleSegmentChange = useCallback(
    (val: string) => {
      handleModeChange(val as TTestMode);
    },
    [handleModeChange],
  );

  /**
   * 加载示例数据
   */
  const handleLoadExample = useCallback((example: string) => {
    setJsonInput(example);
    setValidationReport(null);
    setChartData(null);
  }, []);

  return (
    <div className="dba-test-bi-data-container">
      <div className="dba-test-bi-data-header">
        <h1>biData 图表测试工具</h1>
        <p>支持 biData 业务格式与原生 ECharts options 两种模式的图表数据校验与渲染</p>
      </div>

      <div className="dba-test-bi-data-content">
        {/* 顶部：模式切换（位于 JSON 数据输入卡片上方，使用 tea Segment 默认风格） */}
        <div className="dba-test-bi-data-mode-switch">
          <Segment
            value={mode}
            options={MODE_OPTIONS}
            onChange={handleSegmentChange}
            disabled={isValidating}
          />
        </div>

        {/* 输入区域 */}
        <div className="dba-test-bi-data-input">
          <InputArea
            value={jsonInput}
            mode={mode}
            onChange={setJsonInput}
            onRender={handleRender}
            onClear={handleClear}
            isValidating={isValidating}
          />
        </div>

        {/* 中部：图表渲染区 */}
        <div className="dba-test-bi-data-result">
          {chartData && mode === 'biData' && (
            <ChartPreview key={chartDataKey} data={chartData} />
          )}
          {chartData && mode === 'echartsOption' && (
            <EchartsOptionPreview key={chartDataKey} options={chartData} />
          )}
        </div>

        {/* 校验结果 */}
        <div className="dba-test-bi-data-check">
          {validationReport && <ValidationPanel report={validationReport} />}
        </div>

        {/* 底部：示例数据 */}
        <div className="dba-test-bi-data-examples">
          <ExampleSelector mode={mode} onSelect={handleLoadExample} />
        </div>
      </div>
    </div>
  );
};

export default TestBiData;
